import { NotifyReact, NotifyReactEvents } from 'Phaser/EventBus2';
import { EventStreamPhaser2 } from 'Phaser/GameObjects/CursorMobile';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import { PlayablePhrase } from 'Phaser/Scenes/SightReadingPhaser';
import Logger from 'Utils/Logger';

enum AccuracyState {
  // Phrase started with accuracy >= 95% and current accuracy >= 95%
  Perfect,
  // Current phrase had segments of accuracy < 95% but current accuracy >= 95%
  NextPerfect,
  // Accuracy < 95% for the current phrase
  Normal,
  // Accuracy < 75% for the current phrase
  Bad,
}

interface CallbackFunction {
  callback: (nextPhrase: PlayablePhrase, activePhrase: PlayablePhrase) => void;
  context?: object;
}

/**
 * CAT — Chevron Activation Threshold.
 *
 * Chevrons are gated on difficulty as well as accuracy: a phrase only earns one
 * when its complexity POSITION sits at or above this threshold, so a clean run
 * at trivial complexity does not complete a tier. Normalized complexity on the
 * same [0, 1] scale the recurrence tracks. Mirrors `TierConfig.cat`, which
 * overrides it per tier via {@link ChevronManager.setChevronActivationThreshold}.
 */
export const DEFAULT_CHEVRON_ACTIVATION_THRESHOLD = 0.8;

export default class ChevronManager {
  private phrasesTilNextTier = 4;
  private chevronState: AccuracyState = AccuracyState.Normal;
  private eventStream: EventStreamPhaser2 | OSMDEventStream;
  private firstPhrasePlayed = false;
  private onPerfectFinish?: CallbackFunction;
  private onBadFinish?: CallbackFunction;
  private goalTempo: number;
  private tempoEligible: boolean = true;
  private freePlayMode: boolean = false;
  /** CAT. See {@link DEFAULT_CHEVRON_ACTIVATION_THRESHOLD}. */
  private chevronActivationThreshold: number = DEFAULT_CHEVRON_ACTIVATION_THRESHOLD;
  /**
   * The complexity POSITION of the phrase now playing, pushed by the scene at
   * phrase completion of its predecessor.
   *
   * ⚠️ Was `CR_Min` until the band went centre-anchored (2026-08-02). It is now
   * the band's midpoint, and the change is not cosmetic: with a 0.2-wide band
   * centred on the position, `crMin` tops out at `1 - cvr` = 0.8, so gating on
   * the floor would make CAT 0.8 reachable only at the exact ceiling and
   * chevrons all but unearnable. The position is the value the recurrence
   * steps and the one the floor USED to be under floor anchoring, so gating on
   * it preserves the original rule — chevrons open in the top slice of the
   * model's range — under either anchoring.
   *
   * `undefined` means complexity adjustment is not running (feature flag off,
   * or no band planned yet), in which case the CAT gate is inert and chevron
   * behaviour is exactly what it was before the gate existed.
   */
  private complexityPosition?: number;

  // Customisable phrases till next tier, for use with speeding up/slowing down in repertoire.
  constructor(
    eventStream: EventStreamPhaser2 | OSMDEventStream,
    tempo: number,
    goalTempo: number,
    phrasesTillNextTier: number = 4,
    onGainChevron?: CallbackFunction,
    onLoseChevron?: CallbackFunction,
    freePlayMode: boolean = false,
  ) {
    this.goalTempo = goalTempo;
    this.eventStream = eventStream;
    this.phrasesTilNextTier = phrasesTillNextTier;
    this.onPerfectFinish = onGainChevron;
    this.onBadFinish = onLoseChevron;
    this.freePlayMode = freePlayMode;
  }

  public setGoalTempo(goalTempo: number) {
    this.goalTempo = goalTempo;
  }

  /** Override CAT from the active tier's config. */
  public setChevronActivationThreshold(chevronActivationThreshold: number) {
    if (!Number.isFinite(chevronActivationThreshold)) {
      Logger.warn(`[ChevronManager] ignoring non-finite CAT ${String(chevronActivationThreshold)}`);
      return;
    }
    this.chevronActivationThreshold = chevronActivationThreshold;
  }

  /**
   * Tell the manager where in the tier's complexity spectrum the phrase now
   * playing sits. Call before that phrase can finish and be scored.
   *
   * Takes the band's POSITION, not its floor — see {@link complexityPosition}.
   *
   * Pass `undefined` to disable the CAT gate (complexity adjustment is off).
   */
  public setComplexityPosition(position?: number) {
    if (position !== undefined && !Number.isFinite(position)) {
      Logger.warn(`[ChevronManager] ignoring non-finite complexity position ${String(position)}`);
      return;
    }
    const wasEligible = this.complexityEligible();
    this.complexityPosition = position;
    const nowEligible = this.complexityEligible();
    // Extinguish a pulse the gate will no longer honour. The pulse promises a
    // chevron; if the band has just dropped below CAT, that promise is void and
    // the user should see it withdrawn at the phrase boundary rather than
    // discover it when the chevron fails to land.
    if (wasEligible && !nowEligible) {
      NotifyReactEvents.SetPhraseEligible({ eligible: false });
    } else if (
      !wasEligible &&
      nowEligible &&
      this.firstPhrasePlayed &&
      (this.chevronState === AccuracyState.Perfect ||
        this.chevronState === AccuracyState.NextPerfect)
    ) {
      // Mirror image of the extinguish above: the band just climbed UP through
      // CAT. `onTick` lights the pulse only on the rise INTO NextPerfect, so it
      // cannot re-light from an already-established Perfect/NextPerfect run — a
      // clean climb that never dips below 95% crosses CAT already Perfect and
      // would earn chevrons here without ever flashing. Light it now if the
      // player is sustaining a pulse-worthy run, using onTick's own acc/tempo
      // gate so the promise is one the award can keep.
      const acc = this.eventStream.calcAccuracy();
      if (
        acc !== undefined &&
        acc >= 95 &&
        Math.round(this.eventStream.timeKeeper.getBpm()) >= 72
      ) {
        NotifyReactEvents.SetPhraseEligible({ eligible: true });
      }
    }
  }

  /**
   * The CAT half of the chevron gate: is the phrase hard enough to be worth a
   * chevron? Inert (always true) while no complexity position is known.
   */
  private complexityEligible(): boolean {
    if (this.complexityPosition === undefined) return true;
    return this.complexityPosition >= this.chevronActivationThreshold;
  }

  public setFreePlayMode(freePlayMode: boolean) {
    this.freePlayMode = freePlayMode;
    if (freePlayMode !== this.freePlayMode) {
      this.reset();
    }
  }

  public reset(): void {
    this.phrasesTilNextTier = 4;
    this.chevronState = AccuracyState.Normal;
    this.firstPhrasePlayed = false;
    Logger.debug('[ChevronManager] reset to initial state');
  }

  public onPhraseFinishPlaying(
    playedOnce: boolean,
    nextPhrase?: PlayablePhrase,
    activePhrase?: PlayablePhrase,
  ) {
    Logger.debug(
      `[ChevronManager] finishedPlayingPhrase\n
        Chevron State: ${AccuracyState[this.chevronState]}`,
    );
    this.firstPhrasePlayed = true;
    switch (this.chevronState) {
      case AccuracyState.Normal: {
        const acc = this.eventStream.calcAccuracy();
        if (acc >= 95) {
          this.chevronState = AccuracyState.Perfect;
          // CAT gates the pulse as well as the award. Lighting it below
          // threshold promises a chevron `perfectFinish` will then withhold.
          if (this.tempoEligible && this.complexityEligible()) {
            NotifyReactEvents.SetPhraseEligible({ eligible: true });
          }
        }
        break;
      }
      case AccuracyState.Perfect:
        this.perfectFinish(playedOnce, this.tempoEligible, nextPhrase, activePhrase);
        break;
      case AccuracyState.Bad:
        this.badFinish(nextPhrase, activePhrase);
        break;
      case AccuracyState.NextPerfect:
        this.chevronState = AccuracyState.Perfect;
        break;
    }
    // Can be set to eligible only at the end of the phrase, not during the phrase
    // as any dips below the goal tempo will cause the phrase to be ineligible throughout the phrase
    this.tempoEligible = Math.round(this.eventStream.timeKeeper.getBpm()) >= this.goalTempo;
  }

  private perfectFinish(
    playedOnce: boolean,
    tempoEligible: boolean,
    nextPhrase?: PlayablePhrase,
    activePhrase?: PlayablePhrase,
  ) {
    console.debug('[ChevronManager] perfectFinish', { tempoEligible, nextPhrase, activePhrase });
    // Accuracy got us here (chevronState === Perfect); tempo and free-play are
    // the pre-existing gates. CAT is the added difficulty gate: a perfect run at
    // a complexity below the threshold earns nothing, but also loses nothing.
    if (!playedOnce && tempoEligible && !this.freePlayMode && this.complexityEligible()) {
      this.phrasesTilNextTier = Math.max(this.phrasesTilNextTier - 1, 0);
      Logger.debug(
        `[ChevronManager] gained chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}
        ${nextPhrase}
        ${this.onPerfectFinish}`,
      );
      NotifyReactEvents.SetPhrasesTillNextTier({ phrasesTillNextTier: this.phrasesTilNextTier });
    } else if (!playedOnce && tempoEligible && !this.freePlayMode) {
      // console.debug, matching perfectFinish's entry log above. Logger.debug is
      // muted on dev, so routing this through it made the PR test plan's
      // "chevron suppressed by CAT" console signal unobservable in the only
      // environment where anyone would look for it.
      console.debug(
        `[ChevronManager] chevron suppressed by CAT — position ${String(this.complexityPosition)} ` +
          `< ${this.chevronActivationThreshold}`,
      );
    }
    if (nextPhrase && activePhrase)
      this.onPerfectFinish?.callback.call(this.onPerfectFinish?.context, nextPhrase, activePhrase);
  }

  private badFinish(nextPhrase?: PlayablePhrase, activePhrase?: PlayablePhrase) {
    if (!this.freePlayMode) {
      this.phrasesTilNextTier = Math.min(this.phrasesTilNextTier + 1, 4);
      Logger.debug(
        `[ChevronManager] lost chevron\n
        Phrases till next tier: ${this.phrasesTilNextTier}`,
      );
      NotifyReactEvents.SetPhrasesTillNextTier({ phrasesTillNextTier: this.phrasesTilNextTier });
    }
    if (nextPhrase && activePhrase)
      this.onBadFinish?.callback.call(this.onBadFinish?.context, nextPhrase, activePhrase);
  }

  public onTick(tempo: number) {
    Logger.debug(
      `[ChevronManager] onTick\n
        firstPhrasePlayed: ${this.firstPhrasePlayed}`,
    );
    if (!this.firstPhrasePlayed) return;
    const acc = this.eventStream.calcAccuracy();
    if (acc === undefined) {
      Logger.warn('[ChevronManager] Accuracy undefined!');
      return;
    }
    if (acc < 75) {
      this.chevronState = AccuracyState.Bad;
      NotifyReactEvents.SetPhraseEligible({ eligible: false });
    } else if (acc < 95 && this.chevronState != AccuracyState.Normal) {
      this.chevronState = AccuracyState.Normal;
      NotifyReactEvents.SetPhraseEligible({ eligible: false });
    } else if (
      this.chevronState != AccuracyState.Perfect &&
      this.chevronState != AccuracyState.NextPerfect &&
      tempo >= 72 &&
      // Same CAT gate as the phrase-end pulse above: no promise the award
      // cannot keep. State stays put when the gate is shut, so the pulse
      // resumes on its own once the band climbs back to threshold.
      this.complexityEligible()
    ) {
      NotifyReactEvents.SetPhraseEligible({ eligible: true });
      this.chevronState = AccuracyState.NextPerfect;
    }
  }
}
